import React, { useEffect, useRef } from "react";
import { Box, Button, TextField } from "@mui/material";
import { Loader } from "google-maps";

import { GOOGLE_MAPS_API_KEY } from "../../../constants";
import { Place, PlaceTypes } from "../../../models/Place";

interface Props {
  initId?: number;
  initAddress?: string;
  addressLabel?: string;
  onComplete: (place: Place) => Promise<void>;
  onAddressChange?: (address: string) => Promise<void>;
  onClear: () => void;
  clearButtonVariant?: "contained" | "outlined";
  inputLabelOnTop?: boolean;
}

export const AddressAutocompleteForm = ({
  initId,
  initAddress,
  addressLabel,
  onComplete,
  onClear,
  onAddressChange,
  clearButtonVariant = "contained",
  inputLabelOnTop,
}: Props): JSX.Element => {
  let google: any;
  const addressInputRef = useRef(null);
  let autocomplete: any;
  let autocompleteListener: any;
  let addressInputElement: HTMLInputElement | null;

  useEffect(() => {
    initAutoComplete();
    return () => {
      if (autocompleteListener) {
        google.maps.event.removeListener(autocompleteListener);
      }
    };
  }, []);

  const initAutoComplete = async (): Promise<void> => {
    if (!GOOGLE_MAPS_API_KEY) {
      const error = "could not load google maps script";
      console.error(error);
      return;
    }
    if (!window?.google?.maps) {
      const loader = new Loader(GOOGLE_MAPS_API_KEY, {
        libraries: ["places"],
      });

      // load the google places api script
      google = await loader.load();
    } else {
      google = window.google;
    }
    initAddressInput();
  };

  const initAddressInput = () => {
    addressInputElement = addressInputRef?.current;
    if (google && addressInputElement) {
      initAddress &&
        ((addressInputElement as HTMLInputElement).value = initAddress);
      const options = {
        componentRestrictions: { country: ["us", "ca"] },
        fields: ["address_components", "formatted_address", "place_id"],
        strictBounds: false,
        types: ["address"],
      };

      autocomplete = new google.maps.places.Autocomplete(
        addressInputElement,
        options
      );
      autocompleteListener = autocomplete.addListener(
        "place_changed",
        handleAddressSelect
      );
    }
  };

  // google places auto complete widget
  // https://developers.google.com/maps/documentation/javascript/places-autocomplete
  const handleAddressSelect = () => {
    // Get the place details from the autocomplete object.
    const googlePlace = autocomplete.getPlace();
    // uncomment to debug
    // console.log("place", googlePlace);
    if (addressInputElement && googlePlace.formatted_address) {
      let number: string | undefined,
        street: string | undefined,
        city: string | undefined,
        state: string | undefined,
        county: string | undefined,
        postal_code: string | undefined,
        country: string | undefined,
        address2: string | undefined;
      // pull out the state and country
      for (const component of googlePlace.address_components as google.maps.GeocoderAddressComponent[]) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore remove once typings fixed
        const componentType = component.types[0];
        switch (componentType) {
          case "street_number": {
            number = component.short_name;
            break;
          }
          case "route": {
            street = component.short_name;
            break;
          }
          case "subpremise": {
            address2 = component.short_name;
            break;
          }
          case "locality": {
            city = component.short_name;
            break;
          }
          case "administrative_area_level_1": {
            state = component.short_name;
            break;
          }
          case "administrative_area_level_2": {
            county = component.short_name;
            break;
          }
          case "postal_code": {
            postal_code = component.short_name;
            break;
          }
          case "country":
            country = component.short_name;
            break;
        }
      }

      (addressInputElement as HTMLInputElement).value =
        googlePlace.formatted_address;
      const place = new Place(
        initId,
        PlaceTypes.Primary,
        googlePlace.formatted_address,
        googlePlace.place_id,
        number,
        street,
        address2,
        city,
        state,
        county,
        postal_code,
        country
      );
      onComplete(place);
      onAddressChange?.(googlePlace.formatted_address)
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <TextField
        fullWidth
        InputLabelProps={{ shrink: inputLabelOnTop }}
        label={addressLabel || "Address"}
        margin="normal"
        name="address"
        onKeyPress={(ev: any) => {
          // save enter key for form completion
          if (ev.key === "Enter") {
            ev.preventDefault();
          }
        }}
        onBlur={(evt) => {
          if (evt?.target?.value === "") {
            onClear();
          }
        }}
        type="text"
        variant="outlined"
        inputRef={addressInputRef}
      />
      <Button
        variant={clearButtonVariant}
        color="secondary"
        sx={{
          ml: 2,
          height: "42px",
          minWidth: "100px",
        }}
        onClick={() => {
          if (addressInputRef?.current) {
            (addressInputRef.current as HTMLInputElement).value = "";
          }
          onClear();
        }}
      >
        clear
      </Button>
    </Box>
  );
};
